import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useCompanyName } from "@/lib/company";
import { cn } from "@/lib/utils";
import { Page, PageHeader, Loading, Avatar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { KIND_LABELS } from "@/lib/workKinds";
import { EmailFrame } from "@/pages/owner/DocumentPreview";
import {
  RULE_NAME, zoneLabel, zonedNow, nextDates, dayLabel, thenLine, scheduleHeadline, credits,
} from "@/lib/documents";
import { ArrowLeft, CalendarDays, EyeOff, X } from "lucide-react";

// design_handoff_founder_assistant screen 6 (Scheduler.dc.html), the owner's request: an easy investor update scheduler.
// Three presets (Once, Every Monday, First Monday of the month), a time in the founder's time zone, what goes in (the
// investor sharing settings' hidden blocks can't be ticked; Hiring costs needs Money per objective), their note, and who
// gets it. The Next line follows every change; the live preview is the last investor update prepared, without the
// sections left out. Each run is prepared just before it goes out and sent when it's ready (backend doc_schedules.py).
const UPPER = "text-xs uppercase tracking-[0.09em] text-muted-foreground";
const CARD = "bg-card rounded-[10px] p-5 flex flex-col gap-4";
const TIMES = ["08:00", "09:00", "10:00", "12:00", "17:00"];
const RULES = [["once", "One date and time"], ["every_monday", "Weekly"], ["first_monday", "Monthly"]];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const browserZone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } };
const zones = () => { try { return Intl.supportedValuesOf("timeZone"); } catch { return []; } };
const initials = (name) => (name || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

function nextMonday(tz) {
  const today = zonedNow(tz).date;
  const [y, m, d] = today.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + ((8 - date.getDay()) % 7 || 7));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export default function Scheduler() {
  const { sid } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const carried = useLocation().state?.recipients;  // from the send dialog's Schedule
  const company = useCompanyName();
  const { data: setup } = useQuery({ queryKey: ["schedules-setup"], queryFn: async () => (await api.get("/documents/schedules/setup")).data });
  const { data: saved } = useQuery({ queryKey: ["schedule", sid], enabled: !!sid,
                                     queryFn: async () => (await api.get(`/documents/schedules/${sid}`)).data.schedule });
  const { data: list } = useQuery({ queryKey: ["documents"], queryFn: async () => (await api.get("/documents")).data });
  const [f, setF] = useState(null);
  const [address, setAddress] = useState("");
  const [zoning, setZoning] = useState(false);

  // the form: a new schedule's defaults (first Monday of the month, 09:00, the investors), or the saved one
  useEffect(() => {
    if (f || !setup || (sid && !saved)) return;
    if (saved) {
      setF({ rule: saved.rule, date: saved.date, time: saved.time, tz: saved.tz, blocks: saved.blocks.filter((b) => b !== "note"),
             noteOn: saved.blocks.includes("note"), note: saved.note || "", people: saved.recipients });
      return;
    }
    const tz = browserZone();
    const allowed = Object.fromEntries(setup.blocks.map((b) => [b.key, b.allowed]));
    let blocks = setup.default_blocks.filter((b) => allowed[b]);
    if (!blocks.includes("money")) blocks = blocks.filter((b) => b !== "hiring_costs");
    setF({ rule: "first_monday", date: nextMonday(tz), time: "09:00", tz, blocks, noteOn: true, note: "",
           people: carried?.length ? carried : setup.investors });
  }, [setup, saved]);  // eslint-disable-line react-hooks/exhaustive-deps

  const last = useMemo(() => (list?.documents || []).find((d) => d.type === "investor_update" && d.files?.email
    && ["ready", "sent", "nocheck"].includes(d.status)), [list]);
  const email = useQuery({
    queryKey: ["document-email", last?.id, last?.prepared_at], enabled: !!last,
    queryFn: async () => (await api.get(`/documents/${last.id}/files/email`, { params: { view: true }, responseType: "text" })).data,
  });

  const save = useMutation({
    mutationFn: async (people) => {
      const body = { rule: f.rule, date: f.date, time: f.time, tz: f.tz, blocks: [...(f.noteOn && f.note.trim() ? ["note"] : []), ...f.blocks],
                     note: f.noteOn ? f.note : "", recipients: people.map(({ name, email: e, kind }) => ({ name, email: e, kind })) };
      return (sid ? await api.put(`/documents/schedules/${sid}`, body) : await api.post("/documents/schedules", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["schedule", sid] });
      toast.success(sid ? "Schedule saved." : "Investor update scheduled.");
      navigate("/founder/documents?tab=scheduled");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const eyebrow = [company, "Documents", "Schedule"].filter(Boolean).join(" · ");
  const back = (
    <Link to="/founder/documents?tab=scheduled" className="self-start inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="w-3.5 h-3.5 rtl:-scale-x-100" aria-hidden="true" />Documents
    </Link>
  );
  if (!f || !setup) return <Page className="gap-5">{back}<PageHeader eyebrow={eyebrow} title="Schedule an investor update." /><Loading /></Page>;

  const set = (patch) => setF({ ...f, ...patch });
  const dates = nextDates(f.rule, f.date, f.time, f.tz, 3);
  // f.people: who's ticked; someone who stopped these updates stays unticked (backend doc_schedules optout)
  const stoppedOf = (p) => (saved?.recipients || []).some((r) => r.stopped && r.email.toLowerCase() === p.email.toLowerCase());
  const ticked = f.people.filter((p) => !stoppedOf(p));
  const has = (e) => f.people.some((p) => p.email.toLowerCase() === e.toLowerCase());
  const togglePerson = (p) => {
    if (stoppedOf(p)) return;
    if (has(p.email)) set({ people: f.people.filter((x) => x.email.toLowerCase() !== p.email.toLowerCase()) });
    else set({ people: [...f.people, p] });
  };
  const addAddress = () => {
    const value = address.trim();
    if (!EMAIL.test(value)) { toast.error("Type an email address."); return; }
    if (!has(value)) set({ people: [...f.people, { name: "", email: value, kind: "address" }] });
    setAddress("");
  };
  const blockOn = (key) => f.blocks.includes(key);
  const toggleBlock = (key) => {
    const on = !blockOn(key);
    let blocks = on ? [...f.blocks, key] : f.blocks.filter((b) => b !== key);
    if (key === "money" && !on) blocks = blocks.filter((b) => b !== "hiring_costs");  // the Fee column goes with it
    set({ blocks });
  };
  const listed = [...setup.investors, ...setup.team];
  const typed = f.people.filter((p) => !listed.some((x) => x.email.toLowerCase() === p.email.toLowerCase()));
  // the live preview leaves out the sections that won't go (Hiring costs is a column of Money's table: it stays shown)
  const hidden = ["note", "progress", "money", "plan", "reliability", "delivery"]
    .filter((b) => (b === "note" ? !(f.noteOn && f.note.trim()) : !blockOn(b)));
  const ready = dates.length > 0 && ticked.length > 0 && f.blocks.length > 0;

  return (
    <Page className="gap-5" data-testid="scheduler">
      {back}
      <PageHeader eyebrow={eyebrow} title="Schedule an investor update." status={scheduleHeadline(dates[0], f.time, ticked)} testid="scheduler-header" />
      <div className="flex flex-wrap items-start gap-6">
        <div className="flex-[1_1_480px] max-w-[540px] min-w-0 flex flex-col gap-4">
          <section className={CARD} aria-labelledby="when-title" data-testid="scheduler-when">
            <h2 id="when-title" className="m-0 font-display text-[15px] font-semibold">When</h2>
            <div role="radiogroup" aria-label="When" className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
              {RULES.map(([rule, sub]) => (
                <button key={rule} type="button" role="radio" aria-checked={f.rule === rule} onClick={() => set({ rule })}
                  data-testid={`scheduler-rule-${rule}`}
                  className={cn("flex flex-col gap-0.5 p-3 rounded-[8px] text-start border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring",
                    f.rule === rule ? "border-foreground bg-background" : "border-gr-hairline hover:bg-gr-hover")}>
                  <span className="font-semibold">{RULE_NAME[rule]}</span><span className="text-sm text-muted-foreground">{sub}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-muted-foreground">{f.rule === "once" ? "Date" : "Starts"}</span>
                <span className="relative">
                  <CalendarDays className="absolute start-2.5 top-2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                  <Input type="date" className="ps-8" value={f.date} onChange={(e) => set({ date: e.target.value })} data-testid="scheduler-date" />
                </span>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-sm text-muted-foreground">Time</span>
                <select value={f.time} onChange={(e) => set({ time: e.target.value })} data-testid="scheduler-time"
                  className="h-8 px-2 border border-gr-control-border rounded-md bg-background">
                  {[...new Set([...TIMES, f.time])].sort().map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <span className="flex flex-col gap-1.5">
                <span className="text-sm text-muted-foreground">Time zone</span>
                {zoning ? (
                  <select value={f.tz} onChange={(e) => { set({ tz: e.target.value }); setZoning(false); }} aria-label="Time zone"
                    className="h-8 px-2 border border-gr-control-border rounded-md bg-background" data-testid="scheduler-zone">
                    {[...new Set([f.tz, ...zones()])].map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
                  </select>
                ) : (
                  <span className="h-8 flex items-center gap-2">{zoneLabel(f.tz)}
                    <button type="button" className="font-semibold underline underline-offset-2 hover:opacity-80" onClick={() => setZoning(true)}>Change</button>
                  </span>
                )}
              </span>
            </div>
            <div className="rounded-[8px] bg-gr-surface-2 px-3 py-2.5 flex flex-col gap-0.5" data-testid="scheduler-next">
              {dates.length ? (
                <>
                  <span className="font-semibold">Next: {dayLabel(dates[0])}, {f.time} · {zoneLabel(f.tz)}</span>
                  <span className="text-sm text-muted-foreground">{thenLine(f.rule, dates)}</span>
                </>
              ) : <span className="text-gr-danger">Pick a date and time that haven't passed.</span>}
            </div>
          </section>

          <section className={CARD} aria-labelledby="what-title" data-testid="scheduler-what">
            <h2 id="what-title" className="m-0 font-display text-[15px] font-semibold">What goes in</h2>
            <div className="flex flex-col gap-2.5">
              {setup.blocks.map((b) => {
                const needs = b.key === "hiring_costs" && !blockOn("money");
                const off = !b.allowed || needs;
                return (
                  <label key={b.key} className={cn("flex items-start gap-2.5", b.key === "hiring_costs" && "ps-6", off ? "text-muted-foreground cursor-not-allowed" : "cursor-pointer")}>
                    <input type="checkbox" className="mt-1 w-3.5 h-3.5 accent-[var(--gr-ink)]" checked={!off && blockOn(b.key)} disabled={off}
                      onChange={() => toggleBlock(b.key)} data-testid={`scheduler-block-${b.key}`} />
                    <span className="flex flex-col">
                      <span>{b.label}</span>
                      <span className="text-sm text-muted-foreground">{b.sub}</span>
                      {!b.allowed && (
                        <span className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                          <EyeOff className="w-3.5 h-3.5" aria-hidden="true" />Hidden in your investor settings.
                          <Link to="/founder/settings#investors" className="font-semibold text-foreground underline underline-offset-2">Change investor sharing</Link>
                        </span>
                      )}
                      {b.allowed && needs && <span className="text-sm text-muted-foreground">Needs Money per objective.</span>}
                    </span>
                  </label>
                );
              })}
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" className="mt-1 w-3.5 h-3.5 accent-[var(--gr-ink)]" checked={f.noteOn} onChange={() => set({ noteOn: !f.noteOn })}
                  data-testid="scheduler-note-on" />
                <span className="flex flex-col"><span>A short note from you</span><span className="text-sm text-muted-foreground">Goes first, in your words</span></span>
              </label>
              {f.noteOn && (
                <label className="flex flex-col gap-1.5 ps-6">
                  <span className="text-sm text-muted-foreground">Your note</span>
                  <Textarea rows={4} maxLength={2000} value={f.note} onChange={(e) => set({ note: e.target.value })} data-testid="scheduler-note" />
                </label>
              )}
            </div>
          </section>

          <section className={CARD} aria-labelledby="to-title" data-testid="scheduler-to">
            <h2 id="to-title" className="m-0 font-display text-[15px] font-semibold">To</h2>
            <div className="flex flex-col gap-1">
              {[...listed, ...typed].map((p) => {
                const stopped = stoppedOf(p);
                return (
                  <label key={p.email} className={cn("flex items-center gap-2.5 py-1", stopped ? "text-muted-foreground cursor-not-allowed" : "cursor-pointer")}>
                    <input type="checkbox" className="w-3.5 h-3.5 accent-[var(--gr-ink)]" checked={!stopped && has(p.email)} disabled={stopped}
                      onChange={() => togglePerson(p)} data-testid="scheduler-person" />
                    <Avatar name={p.name || p.email} size={24} />
                    <span className="flex-1 min-w-0 truncate">{p.name || p.email}</span>
                    <span className={UPPER}>{stopped ? "Stopped these updates" : p.kind === "investor" ? "Investor"
                      : p.kind === "team" ? ["Team", KIND_LABELS[p.work_kind]].filter(Boolean).join(" · ") : "Address"}</span>
                    {p.kind === "address" && !stopped && (
                      <button type="button" aria-label={`Remove ${p.email}`} onClick={() => togglePerson(p)}
                        className="w-6 h-6 grid place-items-center rounded-md hover:bg-gr-hover"><X className="w-3.5 h-3.5" aria-hidden="true" /></button>
                    )}
                  </label>
                );
              })}
              {!listed.length && !typed.length && <span className="text-muted-foreground">No investors yet. Add an address.</span>}
            </div>
            <div className="flex gap-2">
              <Input type="email" placeholder="Add an address" aria-label="Add an address" value={address} onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAddress(); } }} data-testid="scheduler-address" />
              <Button type="button" variant="outline" onClick={addAddress}>Add</Button>
            </div>
            <span className="text-sm text-muted-foreground">Investors also get the Monday summary unless they turned it off.</span>
          </section>

          <div className="flex flex-wrap items-center gap-3">
            <span className="flex-1 min-w-[200px] text-sm text-muted-foreground">
              Each update is prepared just before it goes out: {credits(setup.estimate_usd || 0)}.
            </span>
            <Button type="button" variant="ghost" onClick={() => navigate("/founder/documents?tab=scheduled")}>Cancel</Button>
            <Button type="button" aria-disabled={!ready} disabled={save.isPending} data-testid="scheduler-save"
              onClick={() => { if (ready) save.mutate(ticked); }}>Schedule</Button>
          </div>
        </div>

        <aside aria-label="Live preview" className="flex-[999_1_420px] min-w-0 flex flex-col gap-2" data-testid="scheduler-preview">
          <span className={UPPER}>Live preview{dates[0] ? ` · Next: ${dayLabel(dates[0])}, ${f.time}` : ""}</span>
          <div className="max-h-[1240px] overflow-auto rounded-[10px] bg-card p-4">
            {last ? (email.data ? <EmailFrame html={email.data} hidden={hidden} /> : <Loading />)
              : <p className="m-0 text-muted-foreground">The preview shows your last investor update, with what you leave out taken away. None is prepared yet.</p>}
          </div>
        </aside>
      </div>
    </Page>
  );
}
